import { Chip, Popover, Text, TextInput, Tooltip, UnstyledButton, VisuallyHidden } from "@mantine/core";
import { Box, Search, Zap } from "lucide-react";
import { useEffect, useId, useRef, useState, type Ref } from "react";

import type { CatalogImageModel } from "../api/generated/model";
import { GradientIcon } from "./GradientIcon";
import { ROOM_ABOVE, ROOM_BELOW } from "./popover-room";
import { tierWord } from "./cost-tier";
import classes from "./ModelPicker.module.css";

/** What the list shows of a model: every catalog model has these, image, video or sound. */
export type ListedModel = Pick<
  CatalogImageModel,
  "id" | "name" | "description" | "cost_tier" | "is_new" | "maker"
>;

const ALL = "All";
/** Live's chosen model's bolts: indigo 4 to blue 4. */
const CHOSEN_BOLTS = { from: "var(--mantine-color-indigo-4)", to: "var(--mantine-color-blue-4)" };

/** Live's tooltip on a model's bolts: its usual cost against the others, not its price. */
export function tierSentence(tier: number): string {
  return (
    `${tierWord(tier)} token usage compared with the other models here, for a typical generation. ` +
    "The exact cost is shown before you generate."
  );
}

/** Live's search: the name, the line and the maker, in any case. */
function matches(model: ListedModel, query: string): boolean {
  const needle = query.trim().toLowerCase();
  return (
    needle === "" ||
    [model.name, model.description, model.maker].some((text) => text.toLowerCase().includes(needle))
  );
}

interface ModelPickerProps {
  models: readonly ListedModel[];
  value: string;
  onChange: (modelId: string) => void;
  /** Live's cost bolts on each row; its Sounds lists show none (design-ref/sound/SPEC.md). */
  bolts?: boolean;
  /** The button's tooltip: "Model" in the Studio, live's sentence on each Sounds form. */
  tip?: string;
  /**
   * Opens under the button, for a picker near the top of a dialog (the Generate image dialog,
   * #598), where the room above is too short; composers keep live's upward list.
   */
  below?: boolean;
}

/**
 * The composer's model choice, as live's (G1 decision 25; live-models 01 to 11): the button
 * shows the model; its popover opens upward with a search, the makers as filter chips, and a row
 * per model with its line, its cost tier as bolts and "New". It closes on a choice, a click
 * outside, the button again, or Escape (live's ignores Escape; ours closes, as our menus do).
 */
export function ModelPicker(props: ModelPickerProps) {
  const { models, value, onChange, bolts = true, tip = "Model", below = false } = props;
  const [opened, setOpened] = useState(false);
  const chosen = models.find((model) => model.id === value) ?? models[0];
  const titleId = useId();
  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position={below ? "bottom-start" : "top-start"}
      offset={8}
      middlewares={below ? ROOM_BELOW : ROOM_ABOVE}
      trapFocus
      returnFocus
    >
      <Tooltip label={tip} withinPortal={false} disabled={opened} multiline maw={260}>
        <Popover.Target>
          <UnstyledButton
            className={classes.trigger}
            aria-label={`Model: ${chosen?.name ?? ""}`}
            onClick={() => setOpened((open) => !open)}
          >
            <Box size={20} strokeWidth={1.5} aria-hidden />
            <span className={classes.triggerLabel}>{chosen?.name ?? ""}</span>
          </UnstyledButton>
        </Popover.Target>
      </Tooltip>
      <Popover.Dropdown className={classes.dropdown} aria-labelledby={titleId}>
        <VisuallyHidden id={titleId}>Models</VisuallyHidden>
        {/* Mounted while open: each opening starts unfiltered, on the chosen model. */}
        <ModelList
          models={models}
          value={chosen?.id ?? ""}
          bolts={bolts}
          onChoose={(modelId) => {
            onChange(modelId);
            setOpened(false);
          }}
        />
      </Popover.Dropdown>
    </Popover>
  );
}

interface ModelListProps {
  models: readonly ListedModel[];
  value: string;
  bolts: boolean;
  onChoose: (modelId: string) => void;
}

function ModelList({ models, value, bolts, onChoose }: ModelListProps) {
  const [query, setQuery] = useState("");
  const [maker, setMaker] = useState<string>(ALL);
  const chosenRow = useRef<HTMLButtonElement>(null);
  // Live opens scrolled to the chosen model.
  useEffect(() => {
    chosenRow.current?.scrollIntoView?.({ block: "nearest" });
  }, []);
  const makers = [...new Set(models.map((model) => model.maker))];
  const shown = models.filter(
    (model) => (maker === ALL || model.maker === maker) && matches(model, query),
  );
  return (
    <div className={classes.panel}>
      <TextInput
        radius={8}
        aria-label="Search all models"
        placeholder="Search all models"
        leftSection={<Search size={16} aria-hidden />}
        value={query}
        onChange={(event) => setQuery(event.currentTarget.value)}
      />
      <Chip.Group multiple={false} value={maker} onChange={setMaker}>
        <div className={classes.makers} role="radiogroup" aria-label="Filter by brand">
          {[ALL, ...makers].map((each) => (
            <Chip
              key={each}
              value={each}
              type="radio"
              classNames={{ label: classes.maker, iconWrapper: classes.makerIcon }}
            >
              {each}
            </Chip>
          ))}
        </div>
      </Chip.Group>
      <div className={classes.list}>
        {shown.length === 0 ? (
          <Text className={classes.empty}>No model matches.</Text>
        ) : (
          shown.map((model) => (
            <ModelRow
              key={model.id}
              model={model}
              chosen={model.id === value}
              bolts={bolts}
              rowRef={model.id === value ? chosenRow : undefined}
              onClick={() => onChoose(model.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

interface ModelRowProps {
  model: ListedModel;
  chosen: boolean;
  bolts: boolean;
  rowRef: Ref<HTMLButtonElement> | undefined;
  onClick: () => void;
}

/** A model as live lists it: the maker's tile, the name, the bolts, "New", and its line. */
function ModelRow({ model, chosen, bolts, rowRef, onClick }: ModelRowProps) {
  const describedBy = useId();
  const sentence = bolts ? tierSentence(model.cost_tier) : "";
  return (
    <UnstyledButton
      ref={rowRef}
      className={classes.row}
      aria-pressed={chosen}
      aria-label={model.name}
      aria-describedby={describedBy}
      onClick={onClick}
    >
      <span className={classes.logo} aria-hidden>
        {model.maker.slice(0, 1)}
      </span>
      <span className={classes.text}>
        <span className={classes.head}>
          <span className={classes.name}>{model.name}</span>
          {/* In a portal: the list scrolls and clips what overflows it. */}
          {bolts ? (
            <Tooltip label={sentence} multiline w={300}>
              <span className={classes.tier}>
                {Array.from({ length: model.cost_tier }, (_, at) =>
                  chosen ? (
                    <GradientIcon key={at} icon={Zap} size={12} filled colors={CHOSEN_BOLTS} />
                  ) : (
                    <Zap key={at} size={12} fill="currentColor" aria-hidden />
                  ),
                )}
              </span>
            </Tooltip>
          ) : null}
          {model.is_new ? <span className={classes.badge}>New</span> : null}
        </span>
        <span className={classes.description} id={describedBy}>
          {model.description}
          <VisuallyHidden>{`${model.is_new ? " New." : ""}${bolts ? ` ${sentence}` : ""}`}</VisuallyHidden>
        </span>
      </span>
    </UnstyledButton>
  );
}
